import { css, styled } from 'styled-components'

import {
  Breakpoints,
  Colors,
  pxToRem,
  Typography,
} from '@b-stock/fe-core/design-system'

export const cardViewAllLinkStyles = css`
  ${Typography.Body3}
  color: ${Colors.Semantic.BStock.default.lightBackground};
  flex-shrink: 0;
  text-decoration: none;
  white-space: nowrap;

  svg {
    margin-left: 0.375rem;
  }

  &:hover {
    color: ${Colors.Semantic.BStock.onhover};
    text-decoration: underline;
  }
`

// `sidebar` is the compact variant used in the right-column widgets.
export type CardVariant = 'default' | 'sidebar'

export const Card = styled.div`
  background-color: ${Colors.Semantic.Neutral.white};
  border: ${pxToRem(1)} solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.5rem;
  overflow: hidden;
`

// Centers a full-page LDP state (terms, private/visibility errors) in a white
// card on V2 grey body. Wrapped content (e.g. EmptyState) has its own padding.
export const FullPageStateCard = styled(Card)<{ $width?: string }>`
  margin: 5rem auto;
  width: ${(o) => o.$width ?? '32rem'};

  @media ${Breakpoints.max.small} {
    width: calc(100% - 2rem);
    margin: 2rem auto;
  }
`

// Shared header bar for cards built on <Card>: the light-grey strip with the
// uppercase title and a trailing control (chevron / link). Includes the
// bottom border by default; collapsible cards override it when closed.
const cardHeaderBaseStyles = css`
  align-items: center;
  background: ${Colors.Semantic.Neutral.light_grey};
  border: 0 solid ${Colors.Semantic.Neutral.medium_grey};
  border-width: 0 0 ${pxToRem(1)};
  display: flex;
  gap: 1rem;
  justify-content: space-between;
`

export const cardHeaderStyles = css<{ $variant?: CardVariant }>`
  ${cardHeaderBaseStyles}
  padding: ${(o) =>
    o.$variant === 'sidebar'
      ? `${pxToRem(10)} 0.75rem 0.5rem`
      : `${pxToRem(14)} 1rem 0.75rem`};
`

export const CardHeader = styled.div<{ $variant?: CardVariant }>`
  ${cardHeaderStyles}
`

export const CardTitle = styled.h3<{ $variant?: CardVariant }>`
  ${(o) =>
    o.$variant === 'sidebar' ? Typography.Overline1 : Typography.Subtitle1}
  color: ${Colors.Semantic.Neutral.black};
  letter-spacing: 1.28px;
  margin: 0;
  text-transform: uppercase;
`

export const CardBody = styled.div<{ $variant?: CardVariant }>`
  padding: ${(o) => (o.$variant === 'sidebar' ? '0.75rem' : '1rem')};
`

export const CardFooter = styled.div`
  ${cardHeaderBaseStyles}
  border-width: ${pxToRem(1)} 0 0;
  /* Only ever rendered in the sidebar variant. */
  padding: ${pxToRem(10)} 0.75rem 0.5rem;
`
